'use client';
import React, { FC, useEffect, useState } from 'react';
import { LuvImage } from '@/components/ui/LuvImage';
import { getMediaComponent } from '@/components/common/LandingPageHero/utils';
import { RichText } from '@/components';
import { Document } from '@contentful/rich-text-types';
import { ExtendedTypeHomepageHeroFields } from '@/components/common/HomepageHero/HomepageHero';
import classNames from 'classnames';
import styles from '../HomepageHero.module.scss';
import { UniversalCta } from '../../UniversalCta';
import useAuth from '@/context/AuthContext';

export const Offset: FC<ExtendedTypeHomepageHeroFields> = ({
  entryName,
  mobileMediaIsVideo,
  altText,
  typeImage,
  desktopMedia,
  mobileMedia,
  whiteTextDesktop,
  whiteTextMobile,
  subtext = '',
  headerText = '',
  cta,
  secondaryCTA,
  desktopCtaAlignment,
  loggedOutText,
  loggedInText,
}) => {
  const [hydrated, setHydrated] = useState(false);
  const { user } = useAuth();
  const useLoggedInText = !!user && loggedInText;
  const textToDisplay = useLoggedInText ? loggedInText : loggedOutText;

  // This hydrated state is needed to avoid hydration errors due to SSR https://nextjs.org/docs/messages/react-hydration-error#solution-1-using-useeffect-to-run-on-the-client-only
  useEffect(() => {
    setHydrated(true);
  }, []);

  return (
    <section
      data-testid={`homepage-hero-${entryName}`}
      className="relative mx-auto text-center lg:text-left"
    >
      <div className="lg:h-[465px] 2xl:h-[650px]">
        {getMediaComponent({
          media: desktopMedia,
          classes:
            'absolute h-auto w-full bg-cover bg-center lg:h-[465px] 2xl:h-[650px] desktop-homepage-hero-bg hidden lg:block',
        })}

        <div className="lg:relative lg:flex lg:h-full">
          <div className="block lg:hidden">
            {mobileMediaIsVideo ? (
              getMediaComponent({
                media: mobileMedia,
                classes: '',
              })
            ) : (
              <LuvImage
                imageData={typeImage}
                alt={altText}
                height={648}
                width={750}
                loading="eager"
              />
            )}
          </div>
          <div className="hidden lg:block lg:w-1/2"></div>
          <div className="block flex-col justify-center lg:flex lg:w-1/2">
            <div
              className={classNames(
                'mx-auto p-6 lg:max-w-[605px] lg:bg-transparent',
                whiteTextDesktop ? 'lg:text-white' : '',
                whiteTextMobile ? 'text-white' : '',
              )}
            >
              <div className="absolute left-0 top-0 hidden h-full w-full md:block" />
              <div className="relative mb-5 lg:mb-0">
                {subtext && (
                  <h4 className="text-d-base font-bold lg:mb-2 lg:text-lg 2xl:mb-6 2xl:text-h-x4">
                    {subtext}
                  </h4>
                )}
                <h1 className="mb-2 text-h-x3 font-bold lg:mb-4 lg:text-h-x3 2xl:mb-8 2xl:text-hs-x1">
                  {headerText}
                </h1>
                <div
                  className={classNames(
                    'flex flex-col lg:inline-flex',
                    desktopCtaAlignment === 'row'
                      ? 'gap-4 lg:flex-row'
                      : 'gap-3',
                    secondaryCTA && desktopCtaAlignment === 'row'
                      ? 'lg:w-[100%]'
                      : '',
                  )}
                >
                  {cta && (
                    <UniversalCta
                      link={cta}
                      className={secondaryCTA ? 'lg:grow lg:basis-0' : ''}
                    />
                  )}
                  {secondaryCTA && (
                    <UniversalCta
                      link={secondaryCTA}
                      defaultButtonStyle="Secondary"
                      className="lg:grow lg:basis-0"
                    />
                  )}
                </div>
                {textToDisplay && hydrated && (
                  <div
                    className={classNames(
                      `mt-5 ${styles.homepageHero}`,
                      whiteTextDesktop ? 'lg:text-white' : '',
                      whiteTextMobile ? 'text-white' : '',
                      desktopCtaAlignment === 'row' ? 'lg:mt-7' : 'lg:mt-3',
                    )}
                  >
                    <RichText
                      content={textToDisplay as Document}
                      addParagraphMargins={false}
                      openLinkSameWindow={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
